import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { BehaviorSubject, Subject } from 'rxjs';
import { SettingsApiService } from './api/settings.api.service';
import { take } from 'rxjs';

export type TooltipPosition = 'bottom' | 'top' | 'left' | 'right';

export interface TourStep {
  /** Selektor CSS elementu do podświetlenia (null = fullscreen overlay bez highlight) */
  targetSelector: string | null;
  /** Tytuł tooltipa */
  title: string;
  /** Opis w tooltipie */
  description: string;
  /** Pozycja tooltipa względem elementu */
  tooltipPosition: TooltipPosition;
  /** Ścieżka do nawigacji przed wyświetleniem kroku */
  route?: string;
  /** Tekst przycisku akcji (domyślnie "Dalej") */
  actionText?: string;
  /** Krok pomocy — wyświetlany po pominięciu przewodnika */
  isHelpStep?: boolean;
  /** Selektor elementu, który ma zostać kliknięty, aby otworzyć menu (np. mat-menu trigger) */
  openMenuSelector?: string;
  /** Krok oceny przewodnika (ostatni krok z gwiazdkami) */
  isRatingStep?: boolean;
}

export interface TourState {
  active: boolean;
  currentStep: number;
  steps: TourStep[];
  targetRect: DOMRect | null;
  /** Czy trwa przejście między krokami (ukrywa tooltip/highlight) */
  transitioning: boolean;
  /** Czy trwa płynne przesunięcie tooltipa (CSS transition) */
  sliding: boolean;
}

@Injectable({ providedIn: 'root' })
export class GuidedTourService {

  private _state = new BehaviorSubject<TourState>({
    active: false,
    currentStep: 0,
    steps: [],
    targetRect: null,
    transitioning: false,
    sliding: false
  });

  state$ = this._state.asObservable();

  /** Emituje kiedy użytkownik chce połączyć się z KSeF z touru */
  private _connectKsef$ = new Subject<void>();
  connectKsef$ = this._connectKsef$.asObservable();

  private _resizeObserver: ResizeObserver | null = null;
  private _scrollListener: (() => void) | null = null;
  private _resizeListener: (() => void) | null = null;
  private _currentElement: HTMLElement | null = null;
  private _tourStartedAt: number | null = null;
  private _openMenuSelector: string | null = null;

  private readonly _steps: TourStep[] = [
    {
      targetSelector: null,
      title: 'Witaj w przewodniku po systemie 👋',
      description: 'Zróbmy krótki przegląd aplikacji — pokażemy Ci najważniejsze funkcje i podpowiemy, co warto skonfigurować.',
      tooltipPosition: 'bottom',
      actionText: 'Zaczynajmy'
    },
    {
      targetSelector: '[data-nav-id="pulpit"]',
      title: 'Pulpit',
      description: 'To Twój główny panel — tutaj zobaczysz ogłoszenia systemowe, aktualizacje funkcji, statystyki i szybki dostęp do kluczowych funkcji.',
      tooltipPosition: 'bottom',
      route: '/konto/pulpit'
    },
    {
      targetSelector: '[data-nav-id="dokumenty"]',
      title: 'Dokumenty',
      description: 'Tutaj znajdziesz wystawiane dokumenty, wyszukasz konkretny dokument i będziesz monitorował statusy płatności dokumentów.',
      tooltipPosition: 'bottom',
      route: '/konto/dokumenty'
    },
    {
      targetSelector: '[data-tour-id="szukaj-dokumenty"]',
      title: 'Wyszukiwarka',
      description: 'Wpisz numer faktury, NIP kontrahenta lub nazwę firmy, aby szybko znaleźć konkretny dokument.',
      tooltipPosition: 'right',
      route: '/konto/dokumenty'
    },
    {
      targetSelector: '[data-tour-id="widok-dokumenty"]',
      title: 'Widok',
      description: 'Przełączaj widoki, aby od razu wyświetlić szkice, faktury kosztowe lub dokumenty po terminie płatności.',
      tooltipPosition: 'right',
      route: '/konto/dokumenty'
    },
    {
      targetSelector: '[data-tour-id="wystaw-dokument"]',
      title: 'Wystaw dokument',
      description: 'Kliknij w ten przycisk, aby rozpocząć tworzenie nowego dokumentu. Otworzy się formularz wystawiania dokumentu.',
      tooltipPosition: 'bottom',
      route: '/konto/dokumenty'
    },
    {
      targetSelector: '[data-tour-id="typ-dokumentu"]',
      title: 'Typ dokumentu',
      description: 'Wybierz typ dokumentu, który chcesz wystawić — faktura VAT, proforma, korygująca, zaliczkowa i inne.',
      tooltipPosition: 'bottom',
      route: '/konto/dokumenty/wystaw'
    },
    {
      targetSelector: '[data-tour-id="nabywca"]',
      title: 'Nabywca',
      description: 'Wybierz nabywcę z listy kontrahentów lub dodaj nowego. Dane możesz pobrać automatycznie z GUS po NIP.',
      tooltipPosition: 'bottom',
      route: '/konto/dokumenty/wystaw'
    },
    {
      targetSelector: '[data-tour-id="daty-waluta"]',
      title: 'Daty i waluta',
      description: 'Wybierz datę wystawienia, datę sprzedaży oraz walutę dokumentu. Możesz też włączyć zakres dat.',
      tooltipPosition: 'left',
      route: '/konto/dokumenty/wystaw'
    },
    {
      targetSelector: '[data-tour-id="adnotacje"]',
      title: 'Adnotacje',
      description: 'Dodaj adnotacje do dokumentu, np. podstawę zwolnienia z VAT, mechanizm podzielonej płatności, odwrotne obciążenie i inne.',
      tooltipPosition: 'left',
      route: '/konto/dokumenty/wystaw'
    },
    {
      targetSelector: '[data-tour-id="platnosci"]',
      title: 'Sekcja płatności',
      description: 'Uzupełnij dane dotyczące płatności — ustaw status i sposób płatności, numer konta bankowego lub termin zapłaty.',
      tooltipPosition: 'bottom',
      route: '/konto/dokumenty/wystaw'
    },
    {
      targetSelector: '[data-tour-id="pozycje"]',
      title: 'Pozycje na dokumencie',
      description: 'Dodaj pozycje na dokumencie — wpisz nazwę produktu lub usługi i kwotę. Możesz dodać wiele pozycji.',
      tooltipPosition: 'top',
      route: '/konto/dokumenty/wystaw'
    },
    {
      targetSelector: '[data-tour-id="dostosuj"]',
      title: 'Dostosuj dokument',
      description: 'Więcej zaawansowanych opcji znajdziesz w przycisku „Dostosuj" — edytuj dane sprzedawcy, włącz dodatkowe pola i konfiguruj dokument.',
      tooltipPosition: 'bottom',
      route: '/konto/dokumenty/wystaw'
    },
    {
      targetSelector: '[data-tour-id="utworz-dokument"]',
      title: 'Utwórz dokument',
      description: 'Po uzupełnieniu danych kliknij „Utwórz dokument", aby zapisać dokument, dokument domyślnie zapisuje się jako szkic. Po zapisaniu możesz oznaczyć dokument jako wystawiony. Zobaczmy podgląd przykładowego dokumentu.',
      tooltipPosition: 'bottom',
      route: '/konto/dokumenty/wystaw',
      actionText: 'Pokaż podgląd'
    },
    {
      targetSelector: '[data-tour-id="akcje-dokumentu"]',
      title: 'Akcje dokumentu',
      description: 'Po utworzeniu dokumentu masz do dyspozycji wiele opcji — oznacz jako wystawiony, wyślij e-mailem, pobierz PDF, drukuj lub usuń.',
      tooltipPosition: 'left',
      route: '/konto/dokumenty/testowa-faktura/podglad'
    },
    {
      targetSelector: '[data-tour-id="wyslij-ksef"]',
      title: 'Wysyłka do KSeF',
      description: 'Po skonfigurowaniu połączenia z KSeF, tym przyciskiem wyślesz dokument do Krajowego Systemu e-Faktur.',
      tooltipPosition: 'left',
      route: '/konto/dokumenty/testowa-faktura/podglad'
    },
    {
      targetSelector: '[data-tour-id="historia-aktywnosci"]',
      title: 'Historia aktywności',
      description: 'Tutaj znajdziesz pełną historię zmian dokumencie. Kiedy został utworzony, edytowany lub np. wysłany e-mailem.',
      tooltipPosition: 'top',
      route: '/konto/dokumenty/testowa-faktura/podglad'
    },
    // --- Klienci i Produkty ---
    {
      targetSelector: '[data-nav-id="klienci"]',
      title: 'Klienci',
      description: 'Baza Twoich kontrahentów — dodawaj, edytuj i wyszukuj klientów. Dane kontrahenta możesz pobrać automatycznie z GUS po numerze NIP.',
      tooltipPosition: 'bottom',
      route: '/konto/klienci'
    },
    {
      targetSelector: '[data-nav-id="produkty"]',
      title: 'Produkty',
      description: 'Moduł produktów i usług — zdefiniuj produkty z cenami, jednostkami i stawkami VAT, aby szybko dodawać je na fakturach.',
      tooltipPosition: 'bottom',
      route: '/konto/produkty'
    },
    // --- Moje konto ---
    {
      targetSelector: '[data-tour-id="menu-user"]',
      title: 'Menu użytkownika',
      description: 'To jest Twoje menu użytkownika — znajdziesz tu dostęp do ustawień konta i konfiguracji aplikacji.',
      tooltipPosition: 'bottom'
    },
    {
      targetSelector: '[data-tour-id="menu-moje-konto"]',
      title: 'Opcje konta',
      description: 'W menu znajdziesz opcje „Moje konto" i „Ustawienia". Przejdźmy teraz do strony Moje konto.',
      tooltipPosition: 'bottom',
      openMenuSelector: '[data-tour-id="menu-user"]'
    },
    {
      targetSelector: '[data-tour-id="dane-personalne"]',
      title: 'Dane personalne',
      description: 'Uzupełnij swoje dane — imię, nazwisko, e-mail, numer telefonu oraz PESEL. Te informacje mogą być wykorzystywane na dokumentach.',
      tooltipPosition: 'right',
      route: '/konto/moje-konto/dane-konta'
    },
    {
      targetSelector: '[data-tour-id="logo-firmy"]',
      title: 'Logo firmy',
      description: 'Dodaj logo swojej firmy — będzie automatycznie umieszczane na wystawianych fakturach i innych dokumentach.',
      tooltipPosition: 'right',
      route: '/konto/moje-konto/dane-konta'
    },
    {
      targetSelector: '[data-tour-id="konta-bankowe"]',
      title: 'Konta bankowe',
      description: 'Dodaj konta bankowe, które będą widoczne na fakturach. Możesz ustawić domyślne konto, które będzie automatycznie wybierane.',
      tooltipPosition: 'top',
      route: '/konto/moje-konto/dane-konta'
    },
    {
      targetSelector: '[data-tour-id="weryfikacja-2fa"]',
      title: 'Weryfikacja dwuetapowa (2FA)',
      description: 'Włącz 2FA, aby zabezpieczyć swoje konto dodatkowym kodem przy logowaniu. Szczególnie ważne przy integracji z KSeF i wysyłaniu e-faktur.',
      tooltipPosition: 'right',
      route: '/konto/moje-konto/bezpieczenstwo'
    },
    {
      targetSelector: '[data-tour-id="urzadzenia"]',
      title: 'Zalogowane urządzenia',
      description: 'Przeglądaj aktywne sesje i urządzenia zalogowane na Twoim koncie. Możesz wylogować nieznane urządzenia dla bezpieczeństwa.',
      tooltipPosition: 'right',
      route: '/konto/moje-konto/urzadzenia'
    },
    // --- Ustawienia ---
    {
      targetSelector: '[data-tour-id="menu-ustawienia"]',
      title: 'Ustawienia aplikacji',
      description: 'Z menu użytkownika możesz też przejść do „Ustawienia", aby dostosować aplikację do swoich potrzeb. Zobaczmy, co tam znajdziesz.',
      tooltipPosition: 'bottom',
      openMenuSelector: '[data-tour-id="menu-user"]'
    },
    {
      targetSelector: '[data-tour-id="tryb-motyw"]',
      title: 'Motyw aplikacji',
      description: 'Wybierz tryb jasny lub ciemny — dopasuj wygląd aplikacji do swoich preferencji.',
      tooltipPosition: 'right',
      route: '/konto/ustawienia/ogolne'
    },
    {
      targetSelector: '[data-tour-id="numerowanie-dokumentow"]',
      title: 'Numerowanie dokumentów',
      description: 'Włącz lub wyłącz automatyczne numerowanie dokumentów — system będzie nadawał kolejne numery za Ciebie.',
      tooltipPosition: 'right',
      route: '/konto/ustawienia/autouzupelnianie'
    },
    {
      targetSelector: '[data-tour-id="typ-numerowania"]',
      title: 'Typ numerowania',
      description: 'Wybierz, czy numeracja dokumentów resetuje się co miesiąc, czy co rok.',
      tooltipPosition: 'right',
      route: '/konto/ustawienia/autouzupelnianie'
    },
    {
      targetSelector: '[data-tour-id="tryb-cen"]',
      title: 'Tryb wprowadzania cen',
      description: 'Określ, czy ceny pozycji na fakturach wprowadzasz jako kwoty netto czy brutto.',
      tooltipPosition: 'right',
      route: '/konto/ustawienia/autouzupelnianie'
    },
    {
      targetSelector: '[data-tour-id="sprzedawca"]',
      title: 'Dane sprzedawcy',
      description: 'Wybierz, które dane sprzedawcy mają być domyślnie widoczne na nowym dokumencie — NIP, adres, telefon, e-mail i inne.',
      tooltipPosition: 'right',
      route: '/konto/ustawienia/sprzedawca'
    },
    {
      targetSelector: '[data-tour-id="stawki-vat"]',
      title: 'Stawki VAT',
      description: 'Ustaw domyślną stawkę VAT oraz dostosuj listę dostępnych stawek VAT wyświetlanych przy wystawianiu dokumentów.',
      tooltipPosition: 'right',
      route: '/konto/ustawienia/stawki-vat'
    },
    // --- KSeF ---
    {
      targetSelector: '[data-nav-id="inne"]',
      title: 'Sekcja „Inne"',
      description: 'W sekcji „Inne" na pasku nawigacji znajdziesz dodatkowe moduły — m.in. Krajowy System e-Faktur (KSeF).',
      tooltipPosition: 'bottom'
    },
    {
      targetSelector: '[data-nav-id="ksef"]',
      title: 'Krajowy System e-Faktur',
      description: 'Kliknij tutaj, aby przejść do konfiguracji KSeF — połączenia z Krajowym Systemem e-Faktur.',
      tooltipPosition: 'bottom',
      openMenuSelector: '[data-nav-id="inne"]'
    },
    {
      targetSelector: '[data-tour-id="certyfikat-ksef"]',
      title: 'Certyfikat KSeF',
      description: 'Tutaj dodasz swój certyfikat KSeF, który jest wymagany do połączenia z Krajowym Systemem e-Faktur.',
      tooltipPosition: 'bottom',
      route: '/konto/ksef'
    },
    {
      targetSelector: null,
      title: 'To już wszystko! 🎉',
      description: 'Znasz już wszystkie najważniejsze funkcje w naszym systemie. Wystawiaj dokumenty, skonfiguruj połączenie z KSeF i korzystaj z pełni możliwości systemu. Jeśli będziesz potrzebować pomocy — znajdziesz ją w menu „Pomoc" na górze strony. Powodzenia!',
      tooltipPosition: 'bottom',
      route: '/konto/dokumenty'
    },
    {
      targetSelector: null,
      title: 'Oceń przewodnik ⭐',
      description: 'Twoja opinia pomoże nam ulepszyć przewodnik dla przyszłych użytkowników.',
      tooltipPosition: 'bottom',
      isRatingStep: true
    }
  ];

  constructor(
    private _router: Router,
    private _settingsApiService: SettingsApiService
  ) {}

  get steps(): TourStep[] {
    return this._steps;
  }

  get currentState(): TourState {
    return this._state.value;
  }

  /** Uruchamia guided tour */
  start(): void {
    this._tourStartedAt = Date.now();
    this._state.next({
      active: true,
      currentStep: 0,
      steps: this._steps,
      targetRect: null,
      transitioning: false,
      sliding: false
    });
    this._highlightCurrentStep();
  }

  /** Następny krok */
  next(): void {
    const state = this._state.value;
    if (state.transitioning || state.sliding) return;
    if (state.currentStep < state.steps.length - 1) {
      const nextIndex = state.currentStep + 1;
      const currentStep = state.steps[state.currentStep];
      const nextStep = state.steps[nextIndex];
      const currentRoute = this._router.url.split('?')[0];
      const needsNavigation = nextStep.route && currentRoute !== nextStep.route;

      // Slide: same route, oba kroki mają target, brak menu, element widoczny
      let canSlide = false;
      if (!needsNavigation && currentStep.targetSelector && nextStep.targetSelector
          && !nextStep.openMenuSelector && !currentStep.openMenuSelector) {
        const nextEl = document.querySelector(nextStep.targetSelector) as HTMLElement;
        if (nextEl) {
          const r = nextEl.getBoundingClientRect();
          canSlide = r.top >= 0 && r.bottom <= window.innerHeight;
        }
      }

      if (canSlide) {
        // Płynne przesunięcie — CSS transition na tooltip i highlight
        this._state.next({ ...state, currentStep: nextIndex, sliding: true });
        this._updateTargetRect(() => {
          setTimeout(() => {
            this._state.next({ ...this._state.value, sliding: false });
          }, 350);
        });
      } else {
        // Fadeout → zmiana kroku → fadein
        this._state.next({ ...state, transitioning: true });
        setTimeout(() => {
          this._state.next({ ...this._state.value, currentStep: nextIndex });
          this._highlightCurrentStep();
        }, 250);
      }
    }
  }

  /** Poprzedni krok */
  prev(): void {
    const state = this._state.value;
    if (state.transitioning || state.sliding) return;
    if (state.currentStep > 0) {
      const prevIndex = state.currentStep - 1;
      const currentStep = state.steps[state.currentStep];
      const prevStep = state.steps[prevIndex];
      const currentRoute = this._router.url.split('?')[0];
      const needsNavigation = prevStep.route && currentRoute !== prevStep.route;

      let canSlide = false;
      if (!needsNavigation && currentStep.targetSelector && prevStep.targetSelector
          && !prevStep.openMenuSelector && !currentStep.openMenuSelector) {
        const prevEl = document.querySelector(prevStep.targetSelector) as HTMLElement;
        if (prevEl) {
          const r = prevEl.getBoundingClientRect();
          canSlide = r.top >= 0 && r.bottom <= window.innerHeight;
        }
      }

      if (canSlide) {
        this._state.next({ ...state, currentStep: prevIndex, sliding: true });
        this._updateTargetRect(() => {
          setTimeout(() => {
            this._state.next({ ...this._state.value, sliding: false });
          }, 350);
        });
      } else {
        this._state.next({ ...state, transitioning: true });
        setTimeout(() => {
          this._state.next({ ...this._state.value, currentStep: prevIndex });
          this._highlightCurrentStep();
        }, 250);
      }
    }
  }

  /** Kończy tour z oceną */
  finish(rating: number | null = null): void {
    const state = this._state.value;
    const isHelpStep = state.steps[state.currentStep]?.isHelpStep;
    const stepsCompleted = state.steps.length;
    const totalSteps = this._steps.length;
    const durationSeconds = this._tourStartedAt ? Math.round((Date.now() - this._tourStartedAt) / 1000) : null;

    this._cleanup();
    this._state.next({
      active: false,
      currentStep: 0,
      steps: [],
      targetRect: null,
      transitioning: false,
      sliding: false
    });

    // Nie wysyłaj feedbacku ponownie — skipToHelp() już go wysłał
    if (!isHelpStep) {
      this._sendFeedback(rating, true, stepsCompleted, totalSteps, durationSeconds);
    }

    // Jeśli użytkownik jest na stronie demo, wróć do listy dokumentów
    if (this._router.url.includes('testowa-faktura')) {
      this._router.navigateByUrl('/konto/dokumenty');
    }
  }

  /** Kończy tour i otwiera dialog KSeF */
  finishAndConnectKsef(): void {
    this.finish();
    this._connectKsef$.next();
  }

  /** Pomija tour i pokazuje krok z podświetloną ikoną Pomocy */
  skipToHelp(): void {
    const state = this._state.value;
    const stepsCompleted = state.currentStep + 1;
    const totalSteps = this._steps.length;
    const durationSeconds = this._tourStartedAt ? Math.round((Date.now() - this._tourStartedAt) / 1000) : null;

    this._cleanup();
    this._sendFeedback(null, false, stepsCompleted, totalSteps, durationSeconds);

    const helpStep: TourStep = {
      targetSelector: '[data-tour-id="pomoc"]',
      title: 'Przewodnik po systemie 💡',
      description: 'W tym miejscu możesz uruchomić przewodnik ponownie, przejrzeć bazę wiedzy lub skontaktować się z biurem obsługi.',
      tooltipPosition: 'bottom',
      isHelpStep: true
    };

    const showHelp = () => {
      this._state.next({
        active: true,
        currentStep: 0,
        steps: [helpStep],
        targetRect: null,
        transitioning: true,
        sliding: false
      });
      this._highlightCurrentStep();
    };

    // Jeśli użytkownik jest na stronie demo, wróć do dokumentów
    if (this._router.url.includes('testowa-faktura')) {
      this._router.navigateByUrl('/konto/dokumenty').then(() => {
        setTimeout(showHelp, 300);
      });
    } else {
      showHelp();
    }
  }

  /** Podświetla element bieżącego kroku */
  private _highlightCurrentStep(): void {
    const state = this._state.value;
    const step = state.steps[state.currentStep];
    const currentRoute = this._router.url.split('?')[0];
    const needsNavigation = step.route && currentRoute !== step.route;

    // Sprawdź rzeczywisty stan DOM — czy menu jest otwarte i czy target jest w nim widoczny
    const menuPanel = document.querySelector('.cdk-overlay-container .mat-mdc-menu-panel');
    const menuIsOpen = !!menuPanel;

    if (needsNavigation) {
      // Nawigacja — zamknij otwarte menu i przejdź do nowej strony
      this._closeOpenMenu();
      this._router.navigateByUrl(step.route!).then(() => {
        if (step.openMenuSelector) {
          this._openMenuAndHighlight(step.openMenuSelector!, step.targetSelector);
        } else {
          this._waitForElement(step.targetSelector, 300, 10);
        }
      });
    } else if (step.openMenuSelector) {
      // Krok wymaga otwartego menu — sprawdź DOM, czy target jest już widoczny
      const targetInMenu = step.targetSelector && menuPanel?.querySelector(step.targetSelector);

      if (menuIsOpen && targetInMenu) {
        // Menu otwarte i target widoczny — nie zamykaj, tylko podświetl
        this._openMenuSelector = step.openMenuSelector;
        document.body.classList.add('tour-menu-open');
        this._waitForElement(step.targetSelector, 100, 8);
      } else {
        // Zamknij otwarte menu (jeśli jest) i otwórz potrzebne
        if (menuIsOpen) {
          this._closeOpenMenu();
        }
        this._waitForMenuClosed(() => {
          this._openMenuAndHighlight(step.openMenuSelector!, step.targetSelector);
        });
      }
    } else {
      // Krok nie wymaga menu — zamknij jeśli otwarte
      if (menuIsOpen) {
        this._closeOpenMenu();
      }
      this._updateTargetRect(() => {
        this._state.next({ ...this._state.value, transitioning: false });
      });
    }
  }

  /** Otwiera menu i czeka na pojawienie się CDK overlay, potem podświetla target */
  private _openMenuAndHighlight(menuSelector: string, targetSelector: string | null): void {
    this._openMenu(menuSelector);
    // Czekaj aż CDK overlay z menu pojawi się w DOM
    this._waitForMenuOverlay(() => {
      if (targetSelector) {
        this._waitForElement(targetSelector, 100, 8);
      } else {
        // Wyczyść stary targetRect (żeby tooltip się wycentrował/unikał menu)
        this._updateTargetRect(() => {
          this._state.next({ ...this._state.value, transitioning: false });
        });
      }
    });
  }

  /** Czeka na pojawienie się CDK overlay (mat-menu panel) */
  private _waitForMenuOverlay(callback: () => void, retries = 0): void {
    const overlay = document.querySelector('.cdk-overlay-container .mat-mdc-menu-panel');
    if (overlay) {
      callback();
    } else if (retries < 15) {
      setTimeout(() => this._waitForMenuOverlay(callback, retries + 1), 50);
    } else {
      // Menu nie pojawiło się — kontynuuj mimo to
      callback();
    }
  }

  /** Czeka aż CDK overlay (mat-menu panel) zniknie z DOM */
  private _waitForMenuClosed(callback: () => void, retries = 0): void {
    const overlay = document.querySelector('.cdk-overlay-container .mat-mdc-menu-panel');
    if (!overlay) {
      // Menu zamknięte — odczekaj chwilę na zakończenie animacji
      setTimeout(callback, 100);
    } else if (retries < 15) {
      setTimeout(() => this._waitForMenuClosed(callback, retries + 1), 50);
    } else {
      // Menu nie zamknęło się — kontynuuj mimo to
      callback();
    }
  }

  /** Czeka na pojawienie się elementu w DOM (po nawigacji) */
  private _waitForElement(selector: string | null, delay: number, maxRetries: number): void {
    if (!selector) {
      // Krok bez targetu (centered) — pokaż od razu
      this._updateTargetRect(() => {
        this._state.next({ ...this._state.value, transitioning: false });
      });
      return;
    }

    let retries = 0;
    const check = () => {
      const el = document.querySelector(selector);
      if (el) {
        this._updateTargetRect(() => {
          this._state.next({ ...this._state.value, transitioning: false });
        });
      } else if (retries < maxRetries) {
        retries++;
        setTimeout(check, delay);
      } else {
        // Element nie znaleziony — pokaż centered
        this._updateTargetRect(() => {
          this._state.next({ ...this._state.value, transitioning: false });
        });
      }
    };

    setTimeout(check, delay);
  }

  /** Aktualizuje pozycję podświetlonego elementu, wywołuje callback po ustawieniu rect */
  private _updateTargetRect(onReady?: () => void): void {
    const state = this._state.value;
    const step = state.steps[state.currentStep];

    this._cleanupTracking();

    if (!step.targetSelector) {
      this._state.next({ ...state, targetRect: null });
      onReady?.();
      return;
    }

    const el = document.querySelector(step.targetSelector) as HTMLElement;
    if (!el) {
      this._state.next({ ...state, targetRect: null });
      onReady?.();
      return;
    }

    this._currentElement = el;

    // Sprawdź czy element jest już widoczny w viewport
    const initialRect = el.getBoundingClientRect();
    const hasSize = initialRect.width > 0 && initialRect.height > 0;
    const isInView = hasSize && initialRect.top >= 0 && initialRect.bottom <= window.innerHeight;

    if (isInView) {
      // Element widoczny — ustaw rect natychmiast
      this._state.next({ ...this._state.value, targetRect: initialRect });
      onReady?.();
    } else if (!hasSize) {
      // Element nie ma wymiarów (np. w trakcie animacji menu) — poczekaj
      this._state.next({ ...this._state.value, targetRect: null });
      onReady?.();
    } else {
      // Przewiń do elementu i poczekaj na zakończenie scrolla
      el.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'nearest' });
      setTimeout(() => {
        const rect = el.getBoundingClientRect();
        this._state.next({ ...this._state.value, targetRect: rect });
        onReady?.();
      }, 350);
    }

    // Śledź zmiany rozmiaru elementu
    this._resizeObserver = new ResizeObserver(() => {
      const updated = el.getBoundingClientRect();
      if (updated.width > 0 && updated.height > 0) {
        this._state.next({ ...this._state.value, targetRect: updated });
      }
    });
    this._resizeObserver.observe(el);

    // Śledź scroll
    this._scrollListener = () => {
      const updated = el.getBoundingClientRect();
      if (updated.width > 0 && updated.height > 0) {
        this._state.next({ ...this._state.value, targetRect: updated });
      }
    };
    window.addEventListener('scroll', this._scrollListener, true);

    // Śledź resize okna
    this._resizeListener = () => {
      const updated = el.getBoundingClientRect();
      if (updated.width > 0 && updated.height > 0) {
        this._state.next({ ...this._state.value, targetRect: updated });
      }
    };
    window.addEventListener('resize', this._resizeListener);
  }

  private _cleanupTracking(): void {
    this._currentElement = null;
    if (this._resizeObserver) {
      this._resizeObserver.disconnect();
      this._resizeObserver = null;
    }
    if (this._scrollListener) {
      window.removeEventListener('scroll', this._scrollListener, true);
      this._scrollListener = null;
    }
    if (this._resizeListener) {
      window.removeEventListener('resize', this._resizeListener);
      this._resizeListener = null;
    }
  }

  private _cleanup(): void {
    this._cleanupTracking();
    this._closeOpenMenu();
  }

  /** Otwiera menu programatycznie i podnosi z-index CDK overlay */
  private _openMenu(selector: string): void {
    document.body.classList.add('tour-menu-open');
    this._openMenuSelector = selector;
    const trigger = document.querySelector(selector) as HTMLElement;
    trigger?.click();
  }

  /** Zamyka otwarte menu i przywraca z-index */
  private _closeOpenMenu(): void {
    this._openMenuSelector = null;
    document.body.classList.remove('tour-menu-open');
    // Wyślij Escape aby zamknąć ewentualnie otwarte mat-menu
    document.dispatchEvent(new KeyboardEvent('keydown', { key: 'Escape', bubbles: true }));
  }

  private _markSeen(): void {
    this._settingsApiService.markOnboardingSeen()
      .pipe(take(1))
      .subscribe();
  }

  private _sendFeedback(rating: number | null, completed: boolean, stepsCompleted: number, totalSteps: number, durationSeconds: number | null): void {
    const device = this._detectDevice();
    this._settingsApiService.saveTourFeedback({ rating, completed, stepsCompleted, totalSteps, device, durationSeconds })
      .pipe(take(1))
      .subscribe();
  }

  private _detectDevice(): string {
    const ua = navigator.userAgent;
    const isMobile = /Android|iPhone|iPad|iPod|Opera Mini|IEMobile|WPDesktop/i.test(ua);
    return isMobile ? 'mobile_web' : 'web';
  }
}
